if (!customElements.get('product-form')) {
  class ProductForm extends HTMLElement {
    constructor() {
      super();

      this.form = this.querySelector('form');
      this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
      this.cartNotification = document.querySelector('cart-notification');
      this.cartDrawer = document.querySelector('cart-drawer');

      // set variable to know if the Frame pop-up was shown on ATC already - it should only show once
      this.framePopUpOnATCShown = false;
    }

    /**
     * onSubmitHandler
     * @description Handle the product form submission
     */
    async onSubmitHandler(evt) {
      evt.preventDefault();

      const productInfo = this.closest('[data-product-info]');
      const isPhotoPassePartout = this.hasAttribute('data-is-photo-passe-partout');

      // show a pop-up to suggest adding a Frame once & stop the form submission (only when no Frame is selected)
      if (productInfo.querySelector('[data-frame-pop-up-on-atc]')) {
        const frameSelector = '[data-upsell-product="frame"]:not([data-upsell-none])';
        const hasFrames = productInfo.querySelector(frameSelector);
        const checkedFrame = productInfo.querySelector(`${frameSelector}:checked`);

        if (!this.framePopUpOnATCShown && hasFrames && checkedFrame == null) {
          const body = document.querySelector('body');
          body.classList.add('frame-pop-up-on-atc-is-open');
          this.framePopUpOnATCShown = true;

          return;
        }
      }

      this.submitButton = this.querySelector('[type="submit"]');
      this.submitButton.disabled = true;
      this.submitButton.classList.add('button--loading');

      this.cartItems = document.querySelector('cart-items');

      let sectionsToFetch = [
        ...this.cartNotification.getSectionsToRender().map((section) => section.id),
      ];

      if (this.cartItems) {
        sectionsToFetch = [
          ...sectionsToFetch,
          ...this.cartItems.getSectionsToRender().map((section) => section.section),
        ];
        // return unique values of array (it removes the duplicates)
        sectionsToFetch = [...new Set(sectionsToFetch)];
      }

      const formData = new FormData(this.form);
      const mainProductId = +formData.get('id');
      let mainProductQty = +formData.get('quantity');
      let uniqueSKU = buildUniqueSKU(mainProductId);
      const allItems = [];

      // on edit mode, get the quantity from the original product and delete the original product (it will be re-added below with the newly edited options)
      if (sessionStorage.productEdit) {
        const productEditInfo = sessionStorage.productEdit.split('|||');

        // get the original quantity of the product being edited
        mainProductQty = +productEditInfo[0];

        // remove the original product from the cart
        this.cartItems = document.querySelector('cart-items');
        const lineKeys = productEditInfo[1].split(',') || null;
        const cartItemInput = this.cartItems.querySelector(
          `[data-update-keys="${productEditInfo[1]}"]`
        );
        // check if the Cart item still exists (it might have been removed by the user. Even if it was re-added afterwards, it has no match)
        if (cartItemInput)
          await this.cartItems.updateQuantity(lineKeys, cartItemInput.dataset.index, 0);
      }

      // get all the selected upsell products
      const selectedUpsellProducts = Array.from(
        productInfo.querySelectorAll('[data-upsell-product]:not([data-upsell-none])')
      ).filter((radio) => radio.checked);
      // get all the selected upsell product IDs to later create a unique SKU/identifier
      const upsellVariantIDs = selectedUpsellProducts.map(
        (upsellProduct) => upsellProduct.dataset.upsellVariantId
      );

      // add all the selected Upsell products on Add to Cart
      if (selectedUpsellProducts.length > 0) {
        uniqueSKU = buildUniqueSKU(mainProductId, upsellVariantIDs);

        // set new properties if it's a Photo Passe-partout
        let photoPassePartoutProps = {};
        if (isPhotoPassePartout) {
          photoPassePartoutProps = {
            _isPhotoPassePartout: true,
            _mainProduct: `${this.dataset.productHandle}?variant=${mainProductId}`,
          };
        }

        selectedUpsellProducts.forEach((upsellProduct) => {
          allItems.push({
            id: +upsellProduct.dataset.upsellVariantId,
            quantity: mainProductQty,
            properties: {
              _productID: mainProductId,
              _linkedSKU: uniqueSKU,
              _type: upsellProduct.dataset.upsellProduct,
              ...photoPassePartoutProps,
            },
          });
        });
      }

      // Get the main item on ATC
      if (!isPhotoPassePartout) {
        allItems.unshift({
          id: mainProductId,
          quantity: mainProductQty,
          properties: {
            _masterSKU: uniqueSKU,
          },
        });
      }

      // gather the array of items to atc and sections to be rerendered
      const body = JSON.stringify({
        items: allItems,
        sections: sectionsToFetch,
        sections_url: window.location.pathname,
      });

      try {
        const response = await fetch(`${routes.cart_add_url}`, { ...fetchConfig(), ...{ body } });
        const parsedState = await response.json();

        if (response.status === 422) {
          // When quantity error
          this.cartNotification.setActiveElement(document.activeElement);
          this.cartNotification.renderQuantityError(formData.get('inventory_quantity'));
          return;
        }

        // update the cart drawer
        this.cartDrawer.renderContents(parsedState);

        // update the cart items in the cart notification (used for the Cart notification on Remove item)
        this.cartNotification.renderContents(parsedState);

        // add event listeners to the new edit links from the newly added products
        if (document.querySelector('[data-cart-edit-link]')) {
          this.cartItems.updateSessionStorageWithEditProductInfo(
            document.querySelectorAll('[data-cart-edit-link]')
          );
        }

        // Set timeout to force animation. Because content is just updated with renderContents
        setTimeout(() => {
          this.cartDrawer.open();
        });
      } catch (error) {
        console.error(error);
      } finally {
        this.submitButton.classList.remove('button--loading');
        this.submitButton.removeAttribute('disabled');

        // reset the sessionStorage with the Product info of the product being edited and the ATC text
        this.clearEditMode();
      }
    }

    /**
     * clearEditMode
     * @description Clear sessionStorage and all the info about the Edit mode & reset the ATC text
     */
    clearEditMode() {
      sessionStorage.removeItem('productEdit');
      sessionStorage.removeItem('newProductEdit');
      document.querySelector('cart-items').currentProductEdit = undefined;
      document.querySelector('product-info').currentProductEdit = undefined;
      const submitButtonText = this.submitButton.querySelector('[data-add-to-cart-button-text]');
      submitButtonText.textContent = this.submitButton.disabled
        ? window.variantStrings.soldOut
        : window.variantStrings.addToCart;
    }
  }

  window.ProductForm = ProductForm;
  customElements.define('product-form', ProductForm);
}
